/**
 * Locale-aware unit resolution for Femcare Web.
 *
 * FT does NOT store a per-user temperature/weight unit preference (verified via
 * FT OpenAPI `ProfileUpdate` schema — no `temperature_unit`, no `weight_unit`,
 * no `unit_system`). Canonical numeric values are stored raw. Mobile uses the
 * device locale for display; Web mirrors that behaviour by defaulting to the
 * browser locale, with a per-user override persisted to localStorage.
 *
 * Defaults:
 *   - Imperial (en-US, en-LR, en-MM) → °F + lb
 *   - Everyone else                  → °C + kg
 *
 * Public helpers assume FT stores values in metric (°C, kg) which is the
 * universal BBT / cycle-tracker convention. If a FT deployment ever changes
 * that, the conversion functions here are the single point to update.
 */

const IMPERIAL_LOCALES = ["en-US", "en-LR", "en-MM"];
const KEY_TEMP = "fc.unit.temp";   // "C" | "F"
const KEY_MASS = "fc.unit.mass";   // "kg" | "lb"

function localeIsImperial() {
    try {
        const l = (navigator.language || navigator.userLanguage || "").toLowerCase();
        return IMPERIAL_LOCALES.some((x) => l.startsWith(x.toLowerCase()));
    } catch { return false; }
}

export function getTempUnit() {
    try {
        const v = localStorage.getItem(KEY_TEMP);
        if (v === "C" || v === "F") return v;
    } catch {}
    return localeIsImperial() ? "F" : "C";
}
export function setTempUnit(u) { try { localStorage.setItem(KEY_TEMP, u); } catch {} }

export function getMassUnit() {
    try {
        const v = localStorage.getItem(KEY_MASS);
        if (v === "kg" || v === "lb") return v;
    } catch {}
    return localeIsImperial() ? "lb" : "kg";
}
export function setMassUnit(u) { try { localStorage.setItem(KEY_MASS, u); } catch {} }

/** Convert a FT-canonical Celsius value → user's chosen unit. */
export function displayTemp(celsius, unit) {
    const u = unit || getTempUnit();
    if (celsius === null || celsius === undefined || Number.isNaN(Number(celsius))) return { value: "—", unit: `°${u}` };
    const c = Number(celsius);
    if (u === "F") return { value: (c * 9 / 5 + 32).toFixed(1), unit: "°F" };
    return { value: c.toFixed(1), unit: "°C" };
}

/** Convert a FT-canonical kg value → user's chosen unit. */
export function displayMass(kg, unit) {
    const u = unit || getMassUnit();
    if (kg === null || kg === undefined || Number.isNaN(Number(kg))) return { value: "—", unit: u };
    const n = Number(kg);
    if (u === "lb") return { value: (n * 2.2046226218).toFixed(1), unit: "lb" };
    return { value: n.toFixed(1), unit: "kg" };
}
